(() => {
  const storageKey = 'dhwWishlistV1';

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value.filter((item) => item && item.handle) : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch (error) {
      return;
    }
    sync();
    document.dispatchEvent(new CustomEvent('dhw:wishlist:change', { detail: { items } }));
  };

  const productFromButton = (button) => ({
    id: button.dataset.productId || '',
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || `/products/${button.dataset.productHandle}`,
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    vendor: button.dataset.productVendor || ''
  });

  const sync = () => {
    const items = read();
    const handles = new Set(items.map((item) => item.handle));

    document.querySelectorAll('[data-dhw-wishlist-toggle]').forEach((button) => {
      const saved = handles.has(button.dataset.productHandle);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const label = button.querySelector('[data-dhw-wishlist-label]');
      if (label) label.textContent = saved ? 'Saved' : 'Add to wishlist';
      button.setAttribute('aria-label', `${saved ? 'Remove from' : 'Add to'} wishlist: ${button.dataset.productTitle || 'product'}`);
    });

    document.querySelectorAll('[data-dhw-wishlist-count]').forEach((count) => {
      count.dataset.count = String(items.length);
      count.textContent = '';
      count.hidden = items.length === 0;
    });
  };

  const toggle = (product) => {
    const items = read();
    const index = items.findIndex((item) => item.handle === product.handle);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(product);
    write(items);
  };

  const remove = (handle) => write(read().filter((item) => item.handle !== handle));

  const create = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const absoluteUrl = (url) => {
    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      return url;
    }
  };

  const wishlistEmailBody = (items) => {
    const intro = "Here are the pieces I've saved to my wishlist:";
    const products = items.map((item, index) => {
      const details = [item.title, item.price].filter(Boolean).join(' — ');
      return `${index + 1}. ${details}\n${absoluteUrl(item.url)}`;
    });
    return [intro, '', ...products].join('\n');
  };

  const fetchProduct = async (handle) => {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  };

  const buildSizeRow = (card, product) => {
    const row = card.querySelector('[data-dhw-wishlist-size-pills]');
    if (!row || !product) return;

    const sizeOptionIndex = product.options.findIndex((name) => /size/i.test(name));
    const optionKey = sizeOptionIndex >= 0 ? `option${sizeOptionIndex + 1}` : 'option1';

    const seen = new Set();
    product.variants.forEach((variant) => {
      const value = variant[optionKey];
      if (!value || seen.has(value)) return;
      seen.add(value);

      const pill = create('button', 'dhw-wishlist-card__pill', value);
      pill.type = 'button';
      pill.setAttribute('aria-pressed', 'false');
      const available = product.variants.some((v) => v[optionKey] === value && v.available);
      if (!available) pill.disabled = true;

      pill.addEventListener('click', () => {
        row.querySelectorAll('.dhw-wishlist-card__pill').forEach((el) => el.setAttribute('aria-pressed', 'false'));
        pill.setAttribute('aria-pressed', 'true');
        const variant = product.variants.find((v) => v[optionKey] === value && v.available);
        card.dataset.dhwWishlistVariantId = variant ? variant.id : '';
        card.dataset.dhwWishlistSize = value;
      });

      row.append(pill);
    });
  };

  const renderWishlistPage = () => {
    const grid = document.querySelector('[data-dhw-wishlist-grid]');
    const empty = document.querySelector('[data-dhw-wishlist-empty]');
    const share = document.querySelector('[data-dhw-wishlist-share]');
    if (!grid || !empty) return;

    const items = read();
    grid.replaceChildren();
    empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;
    if (share) share.hidden = items.length === 0;

    items.forEach((item) => {
      const card = create('article', 'dhw-wishlist-card');
      card.dataset.dhwWishlistCard = item.handle;

      const imageLink = create('a', 'dhw-wishlist-card__image-link');
      imageLink.href = item.url;
      if (item.image) {
        const image = create('img', 'dhw-wishlist-card__image');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        image.width = 900;
        image.height = 1200;
        imageLink.append(image);
      }

      const body = create('div', 'dhw-wishlist-card__body');

      const head = create('div', 'dhw-wishlist-card__head');
      const headInfo = create('div');
      if (item.vendor) headInfo.append(create('p', 'dhw-wishlist-card__vendor', item.vendor));
      const title = create('a', 'dhw-wishlist-card__title', item.title);
      title.href = item.url;
      headInfo.append(title);
      if (item.price) headInfo.append(create('p', 'dhw-wishlist-card__meta', item.price));
      const removeButton = create('button', 'dhw-wishlist-card__remove', 'Remove');
      removeButton.type = 'button';
      removeButton.dataset.dhwWishlistRemove = item.handle;
      removeButton.setAttribute('aria-label', `Remove ${item.title} from wishlist`);
      head.append(headInfo, removeButton);

      const divider1 = create('hr', 'dhw-wishlist-card__divider');

      const options = create('div', 'dhw-wishlist-card__options dhw-wishlist-card__options--single');
      const dateBlock = create('div');
      dateBlock.append(create('p', 'dhw-wishlist-card__label', 'Wear date'));
      const dateInput = create('input', 'dhw-wishlist-card__date-input');
      dateInput.type = 'date';
      dateBlock.append(dateInput);
      dateBlock.append(create('p', 'dhw-wishlist-card__date-hint', "We'll confirm your delivery window after booking."));
      options.append(dateBlock);

      const divider2 = create('hr', 'dhw-wishlist-card__divider');

      const sizeSection = create('div');
      const sizeRow = create('div', 'dhw-wishlist-card__size-row');
      sizeRow.append(create('p', 'dhw-wishlist-card__label', 'Size'));
      const sizeGuide = create('a', 'dhw-wishlist-card__size-guide', 'Size guide');
      sizeGuide.href = item.url;
      sizeRow.append(sizeGuide);
      const sizePills = create('div', 'dhw-wishlist-card__pills');
      sizePills.dataset.dhwWishlistSizePills = '';
      sizeSection.append(sizeRow, sizePills);

      body.append(head, divider1, options, divider2, sizeSection);
      card.append(imageLink, body);
      grid.append(card);

      fetchProduct(item.handle).then((product) => buildSizeRow(card, product));
    });
  };

  document.addEventListener('click', (event) => {
    const toggleButton = event.target.closest('[data-dhw-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      toggle(productFromButton(toggleButton));
      return;
    }

    const removeButton = event.target.closest('[data-dhw-wishlist-remove]');
    if (removeButton) remove(removeButton.dataset.dhwWishlistRemove);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-dhw-wishlist-share-form]');
    if (!form) return;
    event.preventDefault();

    const emailInput = form.querySelector('[data-dhw-wishlist-share-email]');
    const share = form.closest('[data-dhw-wishlist-share]');
    const status = share && share.querySelector('[data-dhw-wishlist-share-status]');
    const subjectElement = share && share.querySelector('[data-dhw-wishlist-share-subject]');
    const items = read();

    if (!emailInput || !emailInput.reportValidity()) return;
    if (!items.length) {
      if (status) status.textContent = 'Add a piece to your wishlist before sharing it.';
      return;
    }

    const subject = subjectElement ? subjectElement.textContent.trim() : 'My wishlist';
    const mailto = `mailto:${encodeURIComponent(emailInput.value.trim())}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(wishlistEmailBody(items))}`;
    if (status) status.textContent = 'Your email app is opening with your wishlist ready to send.';
    window.location.href = mailto;
  });

  document.addEventListener('dhw:wishlist:change', renderWishlistPage);
  document.addEventListener('shopify:section:load', () => {
    sync();
    renderWishlistPage();
  });
  window.addEventListener('storage', () => {
    sync();
    renderWishlistPage();
  });

  window.DhwWishlist = { get: read, remove, toggle };
  sync();
  renderWishlistPage();
})();
